"use client";

import * as React from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";

import { cn } from "../lib/utils";
import { useRef } from "react";
import TooltipArrowIcon from "../app/shared-components/icons/TooltipArrowIcon";

export function Slider({
  className,
  defaultValue,
  value,
  min = 0,
  max = 100,
  hasMarks = false,
  showTooltip = false,
  minMaxPositionBottom = false,
  formatValue,
  variant = "default",
  ...props
}: React.ComponentProps<typeof SliderPrimitive.Root> & {
  hasMarks?: boolean;
  showTooltip?: boolean;
  minMaxPositionBottom?: boolean;
  /** Formats the end labels and tooltips, e.g. 60000 -> "£60,000". */
  formatValue?: (value: number) => string;
  /** `filters` matches the lifestyle drawer dual-thumb (Figma 15390:21120). */
  variant?: "default" | "filters";
}) {
  const sliderRef = useRef(null);
  const _values = React.useMemo(
    () => (Array.isArray(value) ? value : Array.isArray(defaultValue) ? defaultValue : [min, max]),
    [value, defaultValue, min, max]
  );
  const format = React.useCallback(
    (input: number) => (formatValue ? formatValue(input) : String(input)),
    [formatValue]
  );
  const isFilters = variant === "filters";

  const [showTooltipState, setShowTooltipState] = React.useState(false);
  const space = max && props.step ? max / props.step : 0;

  const handlePointerDown = () => {
    setShowTooltipState(true);
  };

  const handlePointerUp = React.useCallback(() => {
    setShowTooltipState(false);
  }, []);

  React.useEffect(() => {
    document.addEventListener("pointerup", handlePointerUp);
    return () => {
      document.removeEventListener("pointerup", handlePointerUp);
    };
  }, [handlePointerUp]);

  return (
    <SliderPrimitive.Root
      data-slot="slider"
      ref={sliderRef}
      className={cn(
        `relative flex w-full ${minMaxPositionBottom ? "pb-6" : "pt-6"} touch-none items-center select-none data-[disabled]:opacity-50 data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col`,
        isFilters && "pt-0 pb-0 h-9",
        className
      )}
      defaultValue={defaultValue}
      value={value}
      min={min}
      max={max}
      onPointerDown={handlePointerDown}
      {...props}>
      <SliderPrimitive.Track
        className={cn(
          "relative grow overflow-hidden rounded-full data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1",
          isFilters
            ? "bg-[#e7e7e9] data-[orientation=horizontal]:h-1"
            : "bg-[#E7E7E7] data-[orientation=horizontal]:h-[5px]"
        )}>
        <SliderPrimitive.Range className="bg-primary absolute data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full" />
      </SliderPrimitive.Track>

      {!isFilters && (
        <div
          className={`absolute w-full inset-x-0 flex items-center justify-between text-secondary font-jost text-xs font-normal leading-4 ${minMaxPositionBottom ? "bottom-0" : "top-0"}`}>
          <span className={"text-left"}>{format(min)}</span>
          <span className={"text-right"}>{format(max)}</span>
        </div>
      )}

      {hasMarks && (
        <div className="absolute inset-0 flex grow w-full items-center justify-between px-[7px]">
          {Array.from({ length: space + 1 }).map((_, index) => (
            <div className="w-1 h-2 rounded-full bg-primary" key={index}></div>
          ))}
        </div>
      )}

      {/* One thumb per value, per the Radix/shadcn contract: a single-element
          array gives a normal slider, two values give a range. */}
      {_values.map((thumbValue, index) => (
        <SliderPrimitive.Thumb
          key={index}
          className={cn(
            "pointer-events-none block shrink-0 rounded-full transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50 !outline-none",
            isFilters
              ? "flex items-center justify-center size-9 bg-[#e7e7e9] border-0 shadow-none"
              : "bg-white size-[18px] border-[3px] border-primary shadow-[0_3px_8px_0_rgba(0,0,0,0.30)]"
          )}
          onMouseEnter={() => setShowTooltipState(true)}
          onMouseLeave={() => setShowTooltipState(false)}>
          {isFilters && <span className="block size-3 rounded-full bg-primary" aria-hidden="true" />}
          {showTooltip && showTooltipState && (
            <>
              <span className="absolute flex justify-center left-1/2 -translate-x-1/2 -top-8 text-white bg-primary min-w-[38px] w-fit h-6 rounded px-1 py-0.5 whitespace-nowrap text-center font-jost text-sm font-medium leading-5">
                {format(thumbValue)}
              </span>
              <TooltipArrowIcon className="absolute text-primary z-[1005] w-[13px] h-2.5 left-1/2 -translate-x-1/2 -top-3" />
            </>
          )}
        </SliderPrimitive.Thumb>
      ))}
    </SliderPrimitive.Root>
  );
}
